<template>
  <div>
    <div class="row big-list" style="cursor: pointer">

      <div class="col-sm-1 d-none d-md-block p-0 position-relative" @click="poiClicked">
        <span class="rubik-mono"><strong>{{ index }}</strong></span>
      </div>

      <div class="col-12 col-sm-3 d-flex align-items-center justify-content-center position-relative" @click="poiClicked">
        <img class="img-fluid rounded-lg w-100" :src="type.previewImage()" :alt="prettyName" width="239" height="120">
      </div>

      <div class="col-12 col-sm-8 my-2 d-flex flex-column position-relative"> <div class="my-3" @click="poiClicked">
        <p class="display-5 m-0"><strong>{{ prettyName }}</strong></p>
        <p class="text-muted">{{ type.prettyName() }}</p>
        <p class="d-flex align-items-center my-2"><span class="material-icons mr-2 text-yellow">location_on</span><a target="_blank" :href="lngLatUrl" @click.stop>{{ this.lon }} {{ this.lat }}</a></p>
      </div>

        <div v-if="detailsVisible" class="mt-2">
          <div v-if="hasAdditionalDetails">
            <template v-if="expandedPoiData.email">
              <p class="d-flex align-items-center my-2"><span class="material-icons mr-2 text-yellow">email</span> {{ expandedPoiData.email }}</p>
            </template>
            <template v-if="expandedPoiData.phone">
              <p class="d-flex align-items-center my-2"><span class="material-icons mr-2 text-yellow">phone</span>{{expandedPoiData.phone}}</p>
            </template>
            <template v-if="expandedPoiData.website">
              <p class="d-flex align-items-center my-2">
                <span class="material-icons mr-2 text-yellow">language</span>
                <a :href="expandedPoiData.website" target="_blank" rel="noopener noreferrer">{{expandedPoiData.website}}</a>
              </p>
            </template>
          </div>
          <div v-else>
            <p class="text-muted my-2">{{ $t('amenity_app.location_result.no_additional_details_available') }}</p>
          </div>
        </div>

        <div class="mt-auto">
          <button @click.stop="toggleDetails" class="btn btn-sm btn-outline-secondary mt-2">
                <span v-if="isLoadingDetails">
                    <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    {{ $t('shared.loading') }}
                </span>
            <span v-else-if="detailsVisible">{{ $t('amenity_app.location_result.hide_details') }}</span>
            <span v-else>{{ $t('amenity_app.location_result.show_details') }}</span>
          </button>
        </div>

        <span class="result-options" data-bs-toggle="dropdown" aria-expanded="false">
          <div class="card text-center square mr-3 d-flex justify-content-center" style="min-width: 50px;">
            <span>
              <span class="material-icons text-yellow p-1 d-block" style="font-size: 28px;">more_vert</span>
            </span>
          </div>
        </span>
        <div class="dropdown-menu bg-dark mt-2">
          <a class="dropdown-item" :href="poiUrl">{{ $t('amenity_app.location_result.view_on_map') }}</a>
          <span class="dropdown-item" @click="exportToGPX">{{ $t('amenity_app.location_result.export_to_gpx') }}</span>
          <span class="dropdown-item" @click="copyLink">{{ $t('amenity_app.location_result.copy_link') }}</span>
        </div>
      </div> </div> <hr class="my-3">
  </div>
</template>

<script lang="ts">

import { defineComponent } from 'vue';
import { POIUtility } from '../../../utils/models/POI';
import graph_client from "../../../utils/graphql/graph_client";
import {GetPOIByID} from "../../../utils/graphql/poi-graph";
import {geojsonToGpx} from "../../../utils/togpx/geojsonToGpx";

export default defineComponent({
  props: {
    poi: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      expandedPoiData: {},
      detailsVisible: false,   // <-- Add this: Controls visibility of details
      isLoadingDetails: false, // <-- Add this: Shows loading state
    }
  },
  // REMOVE the watch block entirely - we don't want immediate fetching
  // watch: {
  //   poi: {
  //     immediate: true,
  //     handler() {
  //       this.expandedData(); // <-- Remove this immediate call
  //     },
  //   },
  // },
  methods: {
    poiClicked() {
      // Keep map centering logic
      window.map.flyTo({
        center: this.poi.lonlat,
        zoom: 15,
      });
      // Optional: You might want to automatically show details when the item is clicked
      // if (!this.detailsVisible) {
      //   this.toggleDetails();
      // }
    },
    exportToGPX() {
      const feature = {
        type: 'Feature',
        properties: {
          name: (this.poi.name ?? this.type.prettyName()),
          desc: this.otherDetailsString, // Note: otherDetailsString depends on expandedPoiData
                                         // If details haven't loaded, this might be incomplete.
                                         // Consider fetching details before export if needed.
        },
        geometry: {
          type: 'Point',
          coordinates: this.poi.lonlat,
        },
      };

      const gpx = geojsonToGpx(feature);
      const blob = new Blob([gpx], { type: 'application/gpx+xml' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${(this.poi.name ?? this.type.prettyName())}.gpx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    copyLink() {
      navigator.clipboard.writeText(this.poiUrl)
      alert(this.$t('amenity_app.location_result.link_copied_to_clipboard'))
    },
    async expandedData() { // <-- Make async if not already
      // Check if data already exists OR is currently loading
      if (Object.keys(this.expandedPoiData).length > 0 || this.isLoadingDetails) {
        return; // Don't fetch again if we have data or are loading
      }

      this.isLoadingDetails = true; // <-- Set loading state
      this.detailsVisible = false;  // <-- Hide details section while loading (optional)

      try {
        const response = await graph_client.request(GetPOIByID, { // <-- Use await
          id: this.id, type: this.databaseClass,
        });
        // Ensure response.poi exists, default to empty object if not
        this.expandedPoiData = response.poi || {};
        this.detailsVisible = true; // <-- Show details section after successful fetch
      } catch (error) {
        console.error('Error fetching POI data:', error);
        // Optionally handle error display for the user
        this.expandedPoiData = {}; // Clear potentially partial data on error
        this.detailsVisible = false; // Keep details hidden on error
      } finally {
        this.isLoadingDetails = false; // <-- Reset loading state regardless of outcome
      }
    },
    toggleDetails() { // <-- Add this method
      if (!this.detailsVisible) {
        // If details are not visible, check if we need to fetch them
        if (Object.keys(this.expandedPoiData).length === 0) {
          this.expandedData(); // Fetch data
        } else {
          // Data already fetched, just make it visible
          this.detailsVisible = true;
        }
      } else {
        // If details are visible, hide them
        this.detailsVisible = false;
      }
    }
  },
  computed: {
    hasAdditionalDetails() {
      // Check if the expandedPoiData object exists and has any of the specific keys with truthy values
      if (!this.expandedPoiData || Object.keys(this.expandedPoiData).length === 0) {
        return false; // No data loaded yet or empty object
      }
      // Return true if email, phone, or website has a value (not null, undefined, or empty string)
      return !!this.expandedPoiData.email || !!this.expandedPoiData.phone || !!this.expandedPoiData.website;
    },
    type() {
      return POIUtility.fromString(this.poi.sub_type);
    },
    lon() {
      // Safer access assuming poi.location might not always be present or correct
      return Array.isArray(this.poi.location) && this.poi.location.length >= 2 ? this.poi.location[1] : null;
    },
    lat() {
      return Array.isArray(this.poi.location) && this.poi.location.length >= 2 ? this.poi.location[0] : null;
    },
    lngLatUrl() {
      // Return '#' or null if lat/lon are missing
      return (this.lat !== null && this.lon !== null) ? `/map?lat=${this.lat}&lng=${this.lon}` : '#';
    },
    poiUrl() {
      const baseUrl = window.location.origin;
      // Ensure trailUrl getter exists and works as expected
      const trailUrlPart = this.$store.getters['amenities/trailUrl'] || '';
      return `${baseUrl}${trailUrlPart}/map?id=${this.id}&type=${this.databaseClass}`;
    },
    databaseClass() {
      return this.type.databaseClassName();
    },
    prettyName() {
      if (this.poi.name && this.poi.name.toLowerCase() !== 'water') {
        return this.poi.name;
      }
      // Fallback to type name if poi.name is missing, empty, or 'water'
      return this.type.prettyName();
    },
    id() {
      // Add safety check in case id format is different
      return this.poi.id && typeof this.poi.id === 'string' ? this.poi.id.split("POI_")[1] : null;
    },
    otherDetailsString() {
      // This will only be populated *after* details are loaded
      let string = '';
      if (this.expandedPoiData?.email) { // Use optional chaining
        string += this.$t('amenity_app.location_result.gpx_email', { email: this.expandedPoiData.email });
      }
      if (this.expandedPoiData?.website) { // Use optional chaining
        string += ` ${this.$t('amenity_app.location_result.gpx_website', { website: this.expandedPoiData.website })}`;
      }
      if (this.lat !== null && this.lon !== null) {
        string += ` ${this.$t('amenity_app.location_result.gpx_location', { lon: this.lon, lat: this.lat })}`;
      }
      string += ` ${this.$t('amenity_app.location_result.gpx_type', { type: this.type.prettyName() })}`;
      return string.trim(); // Trim leading/trailing whitespace
    },
  },
});

</script>

<style scoped>
.result-options {
  position: absolute;
  top: 0;
  right: 0;
  /* Ensure it doesn't interfere with button */
  margin-top: 0.5rem; /* Adjust as needed */
}
/* Add styles for spinner if needed */
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: .2em;
  vertical-align: -0.125em; /* Align better with text */
}
</style>
